import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild, computed, effect, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { MANGLISH_LOOKUP } from '@otas/utils/directives/extras/manglish-layout';
import { AppManglishDirective, ManglishMode } from '../app-manglish/app-manglish.directive';

/** Precomputed frozen index over the shared layout table (no scans/clones). */
const LAYOUT: Readonly<Record<string, string>> = MANGLISH_LOOKUP;

/** One clickable entry of the Manglish alternates strip. */
interface ManglishAlternate {
    key: string;
    glyph: string;
}

/** One selectable entry of an <app-lookup>. Display text = `[prefix] name [suffix]` trimmed. */
export interface AppLookupOption {
    id: string | number;
    name: string;
    prefix?: string;
    suffix?: string;
    /**
     * Optional category label (e.g. "Expense", "Asset"). When ANY option in
     * the list carries one, a header row renders wherever `group` changes
     * from the previous option — the CALLER controls display order by the
     * order it hands `options` in (this component never reorders/sorts).
     * Ungrouped lists (the default — no option sets `group`) are completely
     * unaffected. Grouped lists always render as a plain (non-virtualised)
     * list — see `isVirtual`.
     */
    group?: string;
    /**
     * Optional secondary text shown muted, right-aligned, in the DROPDOWN ROW
     * only — never part of `label()`, so it never enters the committed input.
     * For legacy-style row metadata (e.g. an item's rate + unit). Does not
     * participate in filtering. Rows without a hint render exactly as before.
     */
    hint?: string;
    /** FontAwesome icon class or Material icon name to render on left side as chip. */
    icon?: string;
    /** Image URL / asset path to render on left side as chip image. */
    imageUrl?: string;
}

/**
 * One pinned FOOTER row of an <app-lookup> dropdown — the AppLookup port of the
 * legacy auto-complete's `<ng-template #settings>` `.auto_suggest_footer` rows
 * (e.g. the booking screen's Pooja Stall / other-income / packaged-pooja
 * shortcuts). Footer rows are NOT options: they never enter the committed
 * value, never participate in filtering/keyboard-highlighting, and a click
 * simply emits `footerSelect` and closes the dropdown.
 */
export interface AppLookupFooterItem {
    id: string | number;
    label: string;
    /** FontAwesome 4 icon name (e.g. 'cart-arrow-down', 'gift', 'fire'). */
    icon?: string;
}

/**
 * Model-driven lookup/autocomplete input (CVA) — the virtualisable
 * replacement pattern for the legacy `app-auto-complete` (whose filtering and
 * selection live on DOM-registered DropdownItemDirectives and therefore
 * cannot be virtualised). Selection state lives entirely in the component
 * model; the dropdown is data-rendered, switching to a CDK virtual scroll
 * viewport at/above `virtualFrom` filtered options.
 *
 * Modes:
 * - Sync: pass `options` — filtered client-side (case-insensitive contains
 *   on `name`, and on `prefix` when present).
 * - Async: pass `search` — the term is debounced 250ms and forwarded to the
 *   callback; a small inline spinner shows while a search is pending.
 *
 * CVA value is the selected option's `id` (null when cleared). `writeValue`
 * resolves the display text from `options` in sync mode (also retro-resolves
 * when `options` arrive later). In async mode there is no id→name lookup, so
 * `writeValue(null)` clears and `writeValue(id)` holds the id with an empty
 * display until an option with that id shows up in `options` or in a search
 * result set — hosts that need an initial label should seed `options`.
 *
 * Keyboard: typing (or ArrowDown when closed) opens; Space on an empty term
 * opens the full list without typing a space; ArrowUp/Down move the
 * highlight (wrapping); Enter commits the highlighted option, or — when none
 * is highlighted — the single exact/startsWith name match; Esc closes without
 * committing; blur commits an exact name match, otherwise clears to null.
 *
 * Manglish (opt-in): set `[manglish]="true"` to layer the standalone
 * `AppManglishDirective` onto the input. Transliterated text flows through the
 * native `input` events the directive dispatches, so filtering/commit/CVA are
 * untouched. While a pattern is active the open dropdown shows an alternates
 * strip (click = what Tab-cycling to it would produce) and a small 'മ' badge
 * indicates manglish mode (F6 toggles).
 *
 * <app-lookup [options]="poojas()" formControlName="poojaId"
 *             (selected)="onPooja($event)"></app-lookup>
 */
@Component({
    selector: 'app-lookup',
    templateUrl: './app-lookup.component.html',
    styleUrls: ['./app-lookup.component.css'],
    imports: [ScrollingModule, AppManglishDirective],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppLookupComponent), multi: true }
    ]
})
export class AppLookupComponent implements ControlValueAccessor, OnDestroy {
    @Input() placeholder = '';
    @Input() tabindex: string | number | null = null;
    @Input() inputId = '';
    /**
     * Native maxlength on the inner input (null = unlimited, the default so
     * every existing consumer is unchanged). Used by free-text consumers whose
     * committed value lands in a bounded DB column — e.g. the booking footer
     * address (person.ADDRESS varchar(255), R80b).
     */
    @Input() maxLength: number | null = null;
    /** Sync mode: full option list, filtered client-side. */
    @Input() set options(value: AppLookupOption[] | null) {
        this.syncOptions.set(value || []);
        this.resolvePendingWrite();
    }
    /** Async mode: debounced (250ms) server-side search. Takes precedence over `options` filtering. */
    @Input() search?: (term: string) => Observable<AppLookupOption[]>;
    /** Switch the dropdown to CDK virtual scrolling at/above this filtered count. */
    @Input() virtualFrom = 50;
    /**
     * Opens the dropdown (all options) as soon as the input gains focus —
     * the AppLookup equivalent of the legacy auto-complete `isForceVisible`
     * setting (used by the booking screens' `isPoojaAutoSuggession` user
     * toggle). Off by default.
     */
    @Input() openOnFocus = false;
    /** Enables the Manglish transliteration layer on the input (off by default). */
    @Input() manglish = false;
    /**
     * Starting Manglish mode, for hosts that already know the typing language.
     * Without it the layer starts in whatever `CURRENT_USER.typingLanguage`
     * says — which a pre-login screen has no answer for, so the മ badge would
     * stay hidden until F6. Null (the default) keeps that stored behaviour.
     */
    @Input() manglishDefault: ManglishMode | null = null;
    /**
     * Free-text mode: the CVA value is the typed TEXT (not an option id), the
     * field is NOT cleared on blur, and picking a suggestion simply fills the
     * text. For inputs like a devotee name that accept arbitrary text with
     * optional suggestions. Off by default.
     */
    @Input() freeText = false;
    /**
     * When true, typing a custom text string that does not match any existing option
     * preserves the typed text as the value instead of clearing it on blur/commit.
     */
    @Input() allowCustomText = false;
    /**
     * Whether the suggestion dropdown may open at all. Set false to suppress it
     * entirely — the component becomes a plain `[manglish]` text input (e.g. the
     * devotee-name field when "Devotee Auto Suggestion" is off). On by default.
     */
    @Input() suggestions = true;
    /**
     * Whether typing auto-highlights the first dropdown item (so a plain Enter
     * commits it). On by default. Set false for free-text fields where Enter
     * should keep the TYPED text and just move on (e.g. the devotee name — the
     * user is entering a name, and any suggestion is a convenience picked
     * deliberately by Arrow/click, never grabbed by an unnoticed Enter).
     */
    @Input() autoHighlightFirst = true;
    /**
     * Pinned footer rows rendered at the BOTTOM of the open dropdown panel —
     * below the options list, in BOTH the plain and virtualised variants, and
     * even when the typed term matches nothing (the legacy footer is always
     * visible while the dropdown is open; it is never filtered). Click-only,
     * like the legacy `.auto_suggest_footer` rows — the ArrowUp/Down highlight
     * arithmetic stays on `filtered()` and never reaches these.
     */
    @Input() footerItems: AppLookupFooterItem[] = [];
    /**
     * Multi-select (checkbox) mode — OFF by default; every single-select
     * behaviour above is untouched when it is false.
     *
     * On: each dropdown row renders a checkbox, clicking a row (or Enter on the
     * highlighted row) TOGGLES it and leaves the dropdown OPEN, and the CVA
     * value is an ARRAY of option ids. The input shows the typed filter text
     * while the panel is open and the selected labels (or 'N selected' when
     * they would not fit / are not resolvable yet) when it is closed. Focus
     * opens the panel, so typing always filters instead of appending to the
     * summary. Blur drops the filter text but NEVER the selection — clearing is
     * unticking (there is no blur-commit / blur-clear in this mode).
     *
     * Following the app-multi-select rule, the ROW's click is the single source
     * of truth and the rendered checkbox is display-only (a label-wrapped or
     * natively-toggling checkbox double-fires and nets no change).
     */
    @Input() multiple = false;

    /** Fires with the committed option, or null when the value is cleared. In
     *  `multiple` mode it fires with the TOGGLED option on every tick/untick. */
    @Output() selected = new EventEmitter<AppLookupOption | null>();
    /** A footer row was clicked: the dropdown closes, the value is untouched. */
    @Output() footerSelect = new EventEmitter<AppLookupFooterItem>();

    readonly term = signal('');
    readonly open = signal(false);
    /** Whether the input currently has focus (gates the manglish strip when the
     *  options dropdown is suppressed). */
    readonly focused = signal(false);
    readonly highlighted = signal(-1);
    readonly pending = signal(false);
    readonly disabled = signal(false);
    readonly itemSize = 32;

    readonly syncOptions = signal<AppLookupOption[]>([]);
    readonly asyncOptions = signal<AppLookupOption[]>([]);
    /** `multiple` mode only: the ticked option ids (the CVA value). */
    readonly selectedIds = signal<(string | number)[]>([]);

    /** Options currently shown in the dropdown. */
    readonly filtered = computed<AppLookupOption[]>(() => {
        if (this.search) {
            return this.asyncOptions();
        }
        const t = this.term().trim().toLowerCase();
        const all = this.syncOptions();
        if (!t) {
            return all;
        }
        return all.filter(o => o.name.toLowerCase().includes(t)
            || (o.prefix ? o.prefix.toLowerCase().includes(t) : false));
    });

    /** Grouped lists (any option carrying `.group`) always render as the plain
     *  list — the virtual-scroll row-height math assumes one uniform-height
     *  row per filtered index, which group header rows would break. Grouped
     *  lists (categories like account heads) are small in practice. */
    readonly isVirtual = computed(() =>
        this.filtered().length >= this.virtualFrom && !this.filtered().some(o => !!o.group));
    readonly viewportHeight = computed(() => Math.min(this.filtered().length, 8) * this.itemSize);

    /**
     * Whether the floating panel (options dropdown OR the Manglish alternates
     * strip) is showing — the single source of truth the template and the
     * positioning effect both read.
     */
    readonly panelVisible = computed(() =>
        this.open() || (this.manglishAlternates().length > 0 && this.focused()));

    /** Longest joined-label summary before falling back to 'N selected'. */
    private static readonly SUMMARY_MAX_CHARS = 44;

    /**
     * `multiple` mode caption when the panel is closed: the ticked labels joined
     * with ', ', or 'N selected' when they would not fit or an id has not been
     * resolved to an option yet (options can arrive after writeValue).
     */
    readonly summaryText = computed(() => {
        const ids = this.selectedIds();
        if (!ids.length) {
            return '';
        }
        // read both pools so the summary recomputes when options land
        const labels = ids.map(id => {
            const option = this.optionById(id);
            return option ? this.label(option) : null;
        });
        if (labels.some(l => l === null)) {
            return ids.length + ' selected';
        }
        const joined = labels.join(', ');
        return joined.length > AppLookupComponent.SUMMARY_MAX_CHARS
            ? ids.length + ' selected'
            : joined;
    });

    /**
     * Text shown in the input. Single mode: the term, exactly as before.
     * Multiple mode: the typed filter term while the panel is open, the
     * selection summary when it is closed.
     */
    readonly displayText = computed(() =>
        this.multiple ? (this.open() ? this.term() : this.summaryText()) : this.term());

    // ---- Floating-panel direction (absolute, auto up/down) -------------------
    // The dropdown stays `position: absolute` inside `.lk-wrap` (like every other
    // shared dropdown, e.g. app-multi-select) — so it is ALWAYS visible the frame
    // it renders and can never get stuck hidden behind a positioning race (a
    // `position:fixed` + rAF-measured version regressed exactly that: the panel
    // and the Manglish strip both vanished inside the modal). The only dynamic
    // bit is the direction: `lk-up` flips it ABOVE the input when there isn't
    // room below. Best-effort — a mis-measure just leaves it below (still shown),
    // never hidden.
    readonly openUp = signal(false);
    /** Rough panel height for the below/above decision (max list + strip). */
    private static readonly PANEL_ESTIMATE_PX = 280;
    private readonly reposition = () => {
        if (this.panelVisible()) {
            this.computeOpenDirection();
        }
    };

    // ---- Manglish layer ----------------------------------------------------
    @ViewChild(AppManglishDirective) private manglishDir?: AppManglishDirective;
    readonly manglishPattern = signal<string | null>(null);
    readonly manglishMode = signal<ManglishMode>('english');

    /**
     * Alternates (base + 2..7) of the active pattern's base, for the strip.
     * Deliberately keeps this component's original probe order (base, +2..+7)
     * rather than consuming MANGLISH_ALTERNATES, whose contract is the legacy
     * suggestion list's table-insertion order - the two differ for exactly
     * one base ('Pa': legacy shows ['Pa2','Pa']). Seven O(1) probes; no scan.
     */
    readonly manglishAlternates = computed<ManglishAlternate[]>(() => {
        const pattern = this.manglishPattern();
        if (!pattern) {
            return [];
        }
        const base = pattern.split(/(\d+)/)[0];
        return [base, base + '2', base + '3', base + '4', base + '5', base + '6', base + '7']
            .filter(key => LAYOUT[key] !== undefined)
            .map(key => ({ key, glyph: LAYOUT[key] }));
    });

    private value: string | number | null = null;
    private pendingWriteId: string | number | null = null;
    private search$ = new Subject<string>();
    private searchSub: Subscription;

    private onChange: (v: string | number | (string | number)[] | null) => void = () => { };
    private onTouched: () => void = () => { };

    constructor(private elementRef: ElementRef) {
        this.searchSub = this.search$.pipe(
            debounceTime(250),
            switchMap(term => {
                if (!this.search || !term.trim()) {
                    return of([] as AppLookupOption[]);
                }
                return this.search(term.trim()).pipe(catchError(() => of([] as AppLookupOption[])));
            })
        ).subscribe(results => {
            this.asyncOptions.set(results || []);
            this.pending.set(false);
            // Highlight the first result so a plain Enter commits it immediately
            // (unless the host opted out via autoHighlightFirst).
            this.highlighted.set((this.autoHighlightFirst && results && results.length && this.term().trim()) ? 0 : -1);
            this.resolvePendingWrite();
        });

        // Only decide the open DIRECTION when the panel is (about to be) shown.
        // Never gates visibility — a mis-measure just leaves it below.
        effect(() => {
            if (this.panelVisible()) {
                this.computeOpenDirection();
            }
        });

        // Recompute direction while open on scroll/resize (available space may
        // change). The panel is `position: absolute` inside `.lk-wrap`, so it
        // already tracks the input on scroll — this only re-picks up/vs/down.
        if (typeof window !== 'undefined') {
            window.addEventListener('scroll', this.reposition, true);
            window.addEventListener('resize', this.reposition);
        }
    }

    ngOnDestroy() {
        this.searchSub.unsubscribe();
        if (typeof window !== 'undefined') {
            window.removeEventListener('scroll', this.reposition, true);
            window.removeEventListener('resize', this.reposition);
        }
    }

    /**
     * Best-effort below/above choice from the input's viewport rect. Sets
     * `openUp` when the estimated panel would overflow the viewport bottom and
     * there is more room above. Never hides the panel — worst case it opens
     * below (still fully shown, just possibly needing a scroll).
     */
    private computeOpenDirection(): void {
        const input = (this.elementRef.nativeElement as HTMLElement)
            .querySelector('.lk-input') as HTMLElement | null;
        if (!input || typeof window === 'undefined') {
            return;
        }
        const rect = input.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const estimate = AppLookupComponent.PANEL_ESTIMATE_PX;
        this.openUp.set(spaceBelow < estimate && spaceAbove > spaceBelow);
    }

    // ---- ControlValueAccessor -------------------------------------------
    writeValue(id: string | number | (string | number)[] | null): void {
        // Multiple: the value is the id ARRAY. Tolerates a comma string or a
        // bare scalar so a host patching either shape still shows a selection.
        if (this.multiple) {
            this.selectedIds.set(normalizeIds(id));
            this.value = null;
            this.pendingWriteId = null;
            this.term.set('');
            return;
        }
        this.value = (id === undefined || Array.isArray(id)) ? null : id;
        // Free-text: the value IS the display text - set it verbatim, no id lookup.
        if (this.freeText) {
            this.term.set(this.value == null ? '' : String(this.value));
            this.pendingWriteId = null;
            return;
        }
        if (this.value === null) {
            this.term.set('');
            this.pendingWriteId = null;
            return;
        }
        this.pendingWriteId = this.value;
        this.term.set('');
        this.resolvePendingWrite();
    }

    registerOnChange(fn: any): void { this.onChange = fn; }
    registerOnTouched(fn: any): void { this.onTouched = fn; }
    setDisabledState(isDisabled: boolean): void {
        this.disabled.set(isDisabled);
        if (isDisabled) {
            this.close();
        }
    }

    // ---- Display ---------------------------------------------------------
    label(option: AppLookupOption): string {
        return [option.prefix, option.name, option.suffix]
            .filter(part => !!part)
            .join(' ')
            .trim();
    }

    /** True when the option at `index` in `filtered()` starts a new `.group`
     *  (including the very first row) — drives the header row before it. Never
     *  reorders the list; the caller controls display order. */
    isGroupStart(index: number): boolean {
        const list = this.filtered();
        const current = list[index]?.group;
        if (!current) {
            return false;
        }
        return index === 0 || list[index - 1]?.group !== current;
    }

    // ---- multiple mode ------------------------------------------------------

    /** `multiple`: whether this option is currently ticked. */
    isOptionSelected(option: AppLookupOption): boolean {
        return this.selectedIds().some(id => String(id) === String(option.id));
    }

    /**
     * `multiple`: ticks/unticks one option and emits the NEW id array. The
     * dropdown deliberately stays open (multi-picking is a sequence of clicks)
     * and the typed filter term is left alone.
     */
    toggleOption(option: AppLookupOption): void {
        const ids = this.selectedIds();
        const next = this.isOptionSelected(option)
            ? ids.filter(id => String(id) !== String(option.id))
            : [...ids, option.id];
        this.selectedIds.set(next);
        this.onChange(next);
        this.selected.emit(option);
    }

    /** Dropdown row click — toggle in `multiple` mode, commit+close otherwise. */
    onOptionClick(option: AppLookupOption): void {
        if (this.multiple) {
            this.toggleOption(option);
            return;
        }
        this.commitOption(option, true);
    }

    /** Alternates-strip click: swap the active glyph via the directive. */
    pickAlternate(patternKey: string) {
        this.manglishDir?.selectAlternate(patternKey);
    }

    /**
     * Footer-row click: emit and close WITHOUT touching the committed
     * value/model — the legacy footer never writes to the control (the caller
     * zeroes the product itself). Blur-commit is already prevented by the
     * dropdown container's `mousedown` preventDefault (same as option rows).
     */
    onFooterClick(item: AppLookupFooterItem) {
        this.footerSelect.emit(item);
        this.close();
    }

    // ---- Input / keyboard --------------------------------------------------
    onInput(raw: string) {
        this.term.set(raw);
        this.pendingWriteId = null;
        // Free-text: the typed text IS the value (a name, not an option id).
        if (this.freeText) {
            this.value = raw;
            this.onChange(raw);
        }
        if (this.canOpen()) {
            this.open.set(true);
        }
        if (this.search) {
            // Async: the first result is highlighted when the debounced set arrives.
            this.highlighted.set(-1);
            // Don't hit the server when suggestions are suppressed.
            if (this.canOpen()) {
                this.pending.set(!!raw.trim());
                this.search$.next(raw);
            }
        } else {
            // Highlight the first match so a plain Enter commits it immediately
            // (owner directive 2026-07-14; opt out via autoHighlightFirst).
            this.highlighted.set(this.autoHighlightFirst && this.canOpen() && raw.trim() && this.filtered().length ? 0 : -1);
        }
    }

    /** The dropdown may open only when suggestions are enabled and not disabled. */
    private canOpen(): boolean {
        return this.suggestions && !this.disabled();
    }

    /** Whether the field holds a usable value: typed text in free-text mode, or a
     *  committed option id otherwise. Gates Enter-to-advance. */
    private hasSettledValue(): boolean {
        if (this.multiple) {
            return this.selectedIds().length > 0;
        }
        return this.freeText ? !!this.term().trim() : this.value !== null;
    }

    onKeydown(event: KeyboardEvent) {
        if (this.disabled()) {
            return;
        }
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (!this.open()) {
                    if (this.canOpen()) {
                        this.open.set(true);
                    }
                    return;
                }
                this.moveHighlight(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (this.open()) {
                    this.moveHighlight(-1);
                }
                break;
            case 'Enter':
                if (this.open()) {
                    event.preventDefault();
                    this.commitOnEnter();
                    // Pre-selected value, dropdown auto-opened on focus, nothing
                    // typed: commitOnEnter had nothing to commit and left it open —
                    // Enter should still confirm the settled value and advance.
                    // (never in `multiple` mode: Enter there is a tick, and the
                    // panel must stay open for the next pick)
                    if (!this.multiple && this.open() && !this.term().trim() && this.hasSettledValue()) {
                        this.close();
                        this.focusNextElement();
                    }
                } else if (this.hasSettledValue()) {
                    // Dropdown closed with a value already settled (a committed
                    // option, OR free-text with content): Enter just advances to the
                    // next field — same as committing does. Previously only the
                    // free-text case advanced, so an option field refocused after an
                    // add (e.g. the booking Vazhipadu) sat stuck on Enter.
                    event.preventDefault();
                    this.focusNextElement();
                }
                break;
            case 'Escape':
                if (this.open()) {
                    event.stopPropagation();
                    this.close();
                }
                break;
            case ' ':
                // Space on an empty/whitespace term opens the full dropdown —
                // the legacy booking screens' "Press <space> …" affordance.
                // preventDefault keeps the leading space out of the input;
                // mid-term spaces pass through untouched (multi-word names).
                // No Manglish conflict: the directive's own keydown handler
                // only resets its pattern on Space (already null here) and
                // never calls preventDefault itself.
                if (!this.term().trim()) {
                    event.preventDefault();
                    if (this.canOpen()) {
                        this.open.set(true);
                    }
                }
                break;
        }
    }

    onFocus() {
        if (this.disabled()) {
            return;
        }
        this.focused.set(true);
        // `multiple` always opens on focus: the closed input shows the selection
        // SUMMARY, so typing into it without opening would append to that text
        // instead of filtering. Opening swaps the input to the (empty) term.
        if ((this.openOnFocus || this.multiple) && this.canOpen()) {
            this.open.set(true);
        }
        // Select the existing text so a single Backspace/Delete clears it and
        // the first keystroke replaces it (owner directive 2026-07-14). Deferred
        // so a mouse-click focus doesn't collapse the selection on mouseup.
        if (!this.multiple && this.term()) {
            const input = this.elementRef.nativeElement.querySelector('input') as HTMLInputElement | null;
            if (input) {
                setTimeout(() => input.select(), 0);
            }
        }
    }

    onBlur() {
        this.focused.set(false);
        if (this.disabled()) {
            return;
        }
        this.onTouched();
        // Multiple: the selection is only ever changed by ticking — blur just
        // drops the filter text (so the summary shows again) and closes.
        if (this.multiple) {
            this.term.set('');
            this.close();
            return;
        }
        // Free-text: the typed text is the value (already emitted on input) - never
        // clear it or coerce it to an option on blur; just close the dropdown.
        if (this.freeText) {
            this.close();
            return;
        }
        const raw = this.term().trim();
        // Already committed and the text is that option's label (the state a
        // click/Enter commit leaves behind — labels include prefix/suffix, so
        // the name matcher below would treat them as junk and clear the value
        // whenever focus moved on): keep the committed value, just close.
        if (this.value !== null) {
            const current = this.optionById(this.value);
            if (current && this.label(current).trim() === raw) {
                this.close();
                return;
            }
        }
        // Match against the full option pool, not filtered(): a typed full
        // label (prefix + name + suffix) never survives the name/prefix
        // filter, but is still an unambiguous selection.
        const pool = this.search ? this.asyncOptions() : this.syncOptions();
        const t = raw.toLowerCase();
        const exact = t
            ? pool.find(o => o.name.toLowerCase() === t || this.label(o).trim().toLowerCase() === t)
            : undefined;
        if (exact) {
            this.commitOption(exact);
        } else if (this.allowCustomText && raw) {
            this.commitCustomText(raw);
        } else if (t || this.value !== null) {
            this.commitOption(null);
        } else {
            this.close();
        }
    }

    commitCustomText(text: string, advanceFocus = false) {
        this.value = text;
        this.term.set(text);
        this.pendingWriteId = null;
        this.onChange(text);
        this.selected.emit({ id: text, name: text });
        this.close();
        if (advanceFocus) {
            this.focusNextElement();
        }
    }

    /**
     * Commits an option (dropdown click / Enter) or clears with null.
     * `advanceFocus` moves focus to the next focusable field — set only for an
     * explicit user pick (Enter / dropdown click), never for a blur-commit or a
     * clear, which must not hijack the focus the user is already moving to.
     */
    commitOption(option: AppLookupOption | null, advanceFocus = false) {
        if (option) {
            // Free-text: the picked suggestion fills the TEXT (the value stays the
            // string); otherwise the value is the option id and the label shows.
            this.value = this.freeText ? option.name : option.id;
            this.term.set(this.freeText ? option.name : this.label(option));
        } else {
            this.value = this.freeText ? '' : null;
            this.term.set('');
        }
        this.pendingWriteId = null;
        this.onChange(this.value);
        this.selected.emit(option);
        this.close();
        if (option && advanceFocus) {
            this.focusNextElement();
        }
    }

    /**
     * Moves focus to the next visible, focusable element after this input in DOM
     * order — so picking an option (Enter / click) advances the form the same way
     * everywhere, with no per-screen focus wiring (owner directive 2026-07-14).
     * Deferred a tick so the closed dropdown is out of the DOM first.
     */
    private focusNextElement() {
        setTimeout(() => {
            const host = this.elementRef.nativeElement as HTMLElement;
            const input = host.querySelector('input') as HTMLElement | null;
            if (!input) {
                return;
            }
            const focusable = Array.from(
                document.querySelectorAll<HTMLElement>('input, select, textarea, button, a[href], [tabindex]'))
                .filter(el => el.tabIndex !== -1
                    && !(el as HTMLInputElement).disabled
                    && el.getClientRects().length > 0 // visible
                    // Skip this component's OWN dropdown descendants (e.g. the Manglish
                    // alternate buttons, which sit right after the input in DOM order and
                    // stay mounted when suggestions are off) — the next field must be a
                    // sibling control, never our own strip. Keep the input itself.
                    && (el === input || !host.contains(el)));
            const here = focusable.indexOf(input);
            if (here !== -1 && focusable[here + 1]) {
                focusable[here + 1].focus();
            }
        }, 0);
    }

    // ---- Internals ---------------------------------------------------------
    private commitOnEnter() {
        const list = this.filtered();
        const index = this.highlighted();
        // Multiple: Enter ticks the highlighted row and keeps the panel open.
        if (this.multiple) {
            if (index >= 0 && index < list.length) {
                this.toggleOption(list[index]);
            }
            return;
        }
        if (index >= 0 && index < list.length) {
            this.commitOption(list[index], true);
            return;
        }
        // Free-text with nothing highlighted (e.g. autoHighlightFirst=false on the
        // devotee name): keep the TYPED text verbatim and just advance — never grab
        // a suggestion the user didn't deliberately pick.
        if (this.freeText) {
            this.close();
            this.focusNextElement();
            return;
        }
        // No highlight: commit the single exact match, else the single
        // startsWith match (mirrors the legacy blur-commit spirit).
        const t = this.term().trim().toLowerCase();
        if (!t) {
            return;
        }
        const exact = list.filter(o => o.name.toLowerCase() === t);
        if (exact.length === 1) {
            this.commitOption(exact[0], true);
            return;
        }
        const starts = list.filter(o => o.name.toLowerCase().startsWith(t));
        if (starts.length === 1) {
            this.commitOption(starts[0], true);
            return;
        }
        if (this.allowCustomText) {
            this.commitCustomText(this.term().trim(), true);
        }
    }

    private moveHighlight(step: 1 | -1) {
        const count = this.filtered().length;
        if (!count) {
            return;
        }
        const current = this.highlighted();
        const next = current < 0
            ? (step === 1 ? 0 : count - 1)
            : (current + step + count) % count; // wraps both directions
        this.highlighted.set(next);
        this.scrollHighlightedIntoView();
    }

    private scrollHighlightedIntoView() {
        const index = this.highlighted();
        if (index < 0) {
            return;
        }
        const host: HTMLElement = this.elementRef.nativeElement;
        if (this.isVirtual()) {
            const viewport = host.querySelector('cdk-virtual-scroll-viewport');
            if (viewport) {
                const top = index * this.itemSize;
                const bottom = top + this.itemSize;
                if (top < viewport.scrollTop) {
                    viewport.scrollTop = top;
                } else if (bottom > viewport.scrollTop + viewport.clientHeight) {
                    viewport.scrollTop = bottom - viewport.clientHeight;
                }
            }
        } else {
            host.querySelectorAll('.lk-list .lk-item')[index]
                ?.scrollIntoView({ block: 'nearest' });
        }
    }

    private close() {
        this.open.set(false);
        this.highlighted.set(-1);
        this.pending.set(false);
    }

    /** The option carrying `id`, from whichever option source knows it. */
    private optionById(id: string | number): AppLookupOption | undefined {
        return this.syncOptions().find(o => o.id === id)
            || this.asyncOptions().find(o => o.id === id);
    }

    /** Resolves a written id to its display text once options are available. */
    private resolvePendingWrite() {
        if (this.pendingWriteId === null) {
            return;
        }
        const match = this.optionById(this.pendingWriteId);
        if (match) {
            this.term.set(this.label(match));
            this.pendingWriteId = null;
        } else if (this.allowCustomText && typeof this.pendingWriteId === 'string') {
            this.term.set(this.pendingWriteId);
            this.pendingWriteId = null;
        }
    }
}

/**
 * `multiple` writeValue coercion: an id array (the canonical shape), a
 * comma-separated string, or a bare scalar all become an id array; null /
 * undefined / '' become an empty one.
 */
function normalizeIds(value: string | number | (string | number)[] | null | undefined): (string | number)[] {
    if (value === null || value === undefined || value === '') {
        return [];
    }
    if (Array.isArray(value)) {
        return value.filter(v => v !== null && v !== undefined && v !== '');
    }
    if (typeof value === 'string' && value.includes(',')) {
        return value.split(',').map(part => part.trim()).filter(part => !!part);
    }
    return [value];
}
